/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ResponseData, showDialog} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useImperativeHandle, useState} from 'react';
import SubscriptionStats from '../../../models/subscriptionStatsModel';
import {ActivateHandle, DataSplitMethod} from '../../../util/interfaces';
import SubscriptionStatsTable from './subscriptionStatsTable';
import {useTranslation} from 'react-i18next';

const LIMIT = 20;

type LoadParams = {page?: number; splitMethod?: DataSplitMethod};

function SubscriptionStatsController(_: object, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [stats, setStats] = useState<Array<SubscriptionStats>>([]);
    const [total, setTotal] = useState(0);
    const [splitMethod, setSplitMethod] = useState(DataSplitMethod.WEEK);

    function activate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
    }

    function onChangePage(nextPage: number) {
        return function () {
            setPage(nextPage);
            load({page: nextPage});
        };
    }

    function onChangeSplitMethod(splitMethod: DataSplitMethod) {
        setSplitMethod(splitMethod);
        load({splitMethod});
    }

    function load(params?: LoadParams) {
        setLoading(true);
        const _page = params?.page ?? page;
        const _splitMethod = params?.splitMethod ?? splitMethod;
        SubscriptionStats.getAll(
            _page,
            LIMIT,
            _splitMethod,
            undefined,
            undefined,
            (stats: SubscriptionStats[], success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    stats.forEach((stat: SubscriptionStats, index: number) => {
                        if (index < stats.length - 1) stat.calculateGain(stats[index + 1]);
                    });
                    setTotal(data.total);
                    setStats(stats);
                } else
                    showDialog(
                        data.message ??
                            t('subscription.stats.errors.load', 'Het laden van de statistieken is niet gelukt.'),
                        t('subscription.stats.errors.loadTitle', 'Laden mislukt')
                    );
            }
        );
    }

    useImperativeHandle(ref, () => ({activate}));

    return (
        <SubscriptionStatsTable
            onClose={onClose}
            onChangePage={onChangePage}
            onChangeSplitMethod={onChangeSplitMethod}
            stats={stats}
            loading={loading}
            active={active}
            page={page}
            total={total}
            splitMethod={splitMethod}
        />
    );
}

export default forwardRef<ActivateHandle, object>(SubscriptionStatsController);
